import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  HStack,
  Text,
  Image,
  IconButton,
  Input,
  useToast,
  AspectRatio,
  Link,
} from "@chakra-ui/react";
import { CloseIcon, SearchIcon } from "@chakra-ui/icons";
import axios from "axios";
import { apiUrl, pexelsEnabled } from "../config";
import PexelsSearchModal from "./PexelsSearchModal";
import LogoCropperModal from "./LogoCropperModal";

const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPTED_MIME = ["image/jpeg", "image/png", "image/webp"];

// Output dimensions for the cropped hero. Matches the recommended source size
// (1920x600, 16:5 landscape) so the hero background renders crisply across
// breakpoints on the campaign sales page.
const HERO_OUTPUT_WIDTH = 1920;
const HERO_OUTPUT_HEIGHT = 600;
const HERO_ASPECT = HERO_OUTPUT_WIDTH / HERO_OUTPUT_HEIGHT;

const HeroImagePicker = ({ value, onChange }) => {
  const fileInputRef = useRef(null);
  const toast = useToast();
  const [uploading, setUploading] = useState(false);
  const [pexelsOpen, setPexelsOpen] = useState(false);
  const [cropSourceUrl, setCropSourceUrl] = useState(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  // When a Pexels photo is sent through the cropper, we stash the attribution
  // here so we can still record it on the campaign after the crop -> upload
  // round-trip replaces the original Pexels URL with our own storage URL.
  const [pendingAttribution, setPendingAttribution] = useState(null);

  const openCropperForFile = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setCropSourceUrl(reader.result);
      setIsCropperOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleFilePick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_MIME.includes(file.type)) {
      toast({
        title: "Unsupported file type",
        description: "Please upload a JPG, PNG, or WebP image.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    if (file.size > MAX_BYTES) {
      toast({
        title: "Image is too large",
        description: "Please choose an image under 10MB.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setPendingAttribution(null);
    openCropperForFile(file);
  };

  const uploadCroppedBlob = async (blob) => {
    const formData = new FormData();
    const file = new File([blob], "hero.jpg", { type: "image/jpeg" });
    formData.append("heroImage", file);
    const idToken = sessionStorage.getItem("idToken");
    const res = await axios.post(
      `${apiUrl}/uploadCampaignHeroImage`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return res.data.url;
  };

  const onCropApplied = async (blob /* , dataUrl */) => {
    setIsCropperOpen(false);
    setCropSourceUrl(null);
    setUploading(true);
    try {
      const url = await uploadCroppedBlob(blob);
      const attribution = pendingAttribution;
      onChange(
        attribution
          ? { url, source: "pexels", attribution }
          : { url, source: "upload" }
      );
      toast({
        title: "Image uploaded",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (err) {
      console.error("Hero image upload failed", err);
      toast({
        title: "Upload failed",
        description:
          err.response?.data?.error ||
          "Could not upload the image. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setPendingAttribution(null);
      setUploading(false);
    }
  };

  const onCropCancel = () => {
    setIsCropperOpen(false);
    setCropSourceUrl(null);
    setPendingAttribution(null);
  };

  const handlePexelsClick = () => {
    if (!pexelsEnabled) {
      toast({
        title: "Coming soon",
        description:
          "The Pexels image library will be available in an upcoming release.",
        status: "info",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    setPexelsOpen(true);
  };

  // Pull the selected Pexels photo down as a blob, hand it to the cropper, and
  // stash the attribution so the eventual upload still records the photographer
  // credit. The data URL the cropper renders is same-origin, so canvas tainting
  // doesn't bite us the way it does for remote storage URLs.
  const handlePexelsPick = async (photo) => {
    try {
      const response = await fetch(photo.fullUrl);
      if (!response.ok) {
        throw new Error(`Pexels image fetch failed (${response.status})`);
      }
      const blob = await response.blob();
      const file = new File([blob], `pexels-${photo.id}.jpg`, {
        type: blob.type || "image/jpeg",
      });
      setPendingAttribution({
        photographer: photo.photographer,
        photographerUrl: photo.photographerUrl,
        pexelsUrl: photo.pexelsUrl,
      });
      setPexelsOpen(false);
      openCropperForFile(file);
    } catch (err) {
      console.error("Failed to load Pexels image", err);
      toast({
        title: "Could not load image",
        description: "Please try a different photo.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleRemove = () => {
    onChange(null);
  };

  return (
    <Box width="100%">
      <Input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileChange}
        display="none"
      />

      {value?.url ? (
        <Box position="relative" mb={3} width="100%">
          <AspectRatio ratio={16 / 5} width="100%">
            <Image
              src={value.url}
              alt="Hero background preview"
              objectFit="cover"
              borderRadius="md"
              borderWidth="1px"
              borderColor="gray.200"
            />
          </AspectRatio>
          <IconButton
            aria-label="Remove hero image"
            icon={<CloseIcon boxSize={3} />}
            size="sm"
            position="absolute"
            top={2}
            right={2}
            onClick={handleRemove}
            colorScheme="blackAlpha"
            bg="blackAlpha.700"
            color="white"
            _hover={{ bg: "blackAlpha.800" }}
          />
          {value.source === "pexels" && value.attribution?.photographer && (
            <Text fontSize="xs" color="gray.600" mt={1}>
              Photo by{" "}
              <Link
                href={value.attribution.photographerUrl}
                isExternal
                color="blue.600"
              >
                {value.attribution.photographer}
              </Link>{" "}
              on{" "}
              <Link
                href={value.attribution.pexelsUrl}
                isExternal
                color="blue.600"
              >
                Pexels
              </Link>
            </Text>
          )}
        </Box>
      ) : (
        <Box
          mb={3}
          width="100%"
          borderWidth="1px"
          borderStyle="dashed"
          borderColor="gray.300"
          borderRadius="md"
          py={10}
          textAlign="center"
          color="gray.500"
          fontSize="sm"
        >
          No hero image selected
        </Box>
      )}

      <HStack spacing={3} flexWrap="wrap">
        <Button
          onClick={handleFilePick}
          isLoading={uploading}
          loadingText="Uploading"
          size="sm"
        >
          {value?.url ? "Replace with upload" : "Upload image"}
        </Button>
        <Button
          onClick={handlePexelsClick}
          leftIcon={<SearchIcon />}
          size="sm"
          variant="outline"
        >
          Choose from Pexels
        </Button>
      </HStack>

      {pexelsEnabled && (
        <PexelsSearchModal
          isOpen={pexelsOpen}
          onClose={() => setPexelsOpen(false)}
          onPick={handlePexelsPick}
        />
      )}

      <LogoCropperModal
        isOpen={isCropperOpen}
        sourceUrl={cropSourceUrl}
        onCancel={onCropCancel}
        onApply={onCropApplied}
        aspect={HERO_ASPECT}
        outputWidth={HERO_OUTPUT_WIDTH}
        outputHeight={HERO_OUTPUT_HEIGHT}
        title="Crop your hero image"
        description={`Drag the corners to resize, or move the box to choose what part of your image will appear at the top of the campaign sales page. The crop is locked to the ${HERO_OUTPUT_WIDTH}×${HERO_OUTPUT_HEIGHT} hero shape.`}
      />
    </Box>
  );
};

export default HeroImagePicker;
